import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import { styles as sharedStyles } from '../styles/shared-styles';
import {
  money,
  currencyDenominations,
  activeCurrency,
} from '../datatypes/currency';

// sl-input exposes a `value` property like a native input, so we can
// type-cast the ref to the minimum shape we need without pulling the
// whole Shoelace TypeScript surface.
type SlInputLike = HTMLElement & { value: string };

// Compact cash-register dialog. Helps the cashier compute change for a
// cash sale without leaving the app. Opens when the cashier hits "Take
// payment" with payment method = cash.
//
// One source of truth: an `amountGiven` number. Tapping a denomination
// button adds its value; typing in the input replaces the value; Reset
// clears it. Change = amountGiven - total. "Ring up sale" emits
// `cash-confirmed` to the parent (which calls its existing _submit).
//
// Denominations + money symbol come from the active shop's currency
// (Phase J1, `datatypes/currency`), not a hardcoded peso list.
//
// UI is intentionally tight to fit a phone screen.

@customElement('cash-register-dialog')
export class CashRegisterDialog extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
      }

      sl-dialog::part(panel) {
        max-width: min(380px, 95vw);
      }

      sl-dialog::part(body) {
        padding: 0.75rem 1rem;
      }

      .total-line {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        padding: 0.4rem 0.6rem;
        background: var(--sl-color-neutral-100);
        border-radius: var(--sl-border-radius-medium);
        margin-bottom: 0.6rem;
        font-size: 0.9rem;
      }

      .total-line .label {
        color: var(--sl-color-neutral-700);
      }

      .total-line .amount {
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }

      sl-input {
        margin-bottom: 0.5rem;
      }

      .denoms {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 0.4rem;
        margin-bottom: 0.6rem;
      }

      .denoms sl-button::part(base) {
        width: 100%;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
      }

      .reset-row {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 0.6rem;
      }

      .change {
        padding: 0.6rem 0.75rem;
        border-radius: var(--sl-border-radius-medium);
        background: var(--sl-color-neutral-50);
        border: 1px solid var(--sl-color-neutral-200);
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }

      .change .label {
        font-size: 0.8rem;
        color: var(--sl-color-neutral-700);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .change .amount {
        font-size: 1.4rem;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }

      .change.positive {
        background: var(--sl-color-success-50);
        border-color: var(--sl-color-success-300);
      }

      .change.positive .amount {
        color: var(--sl-color-success-700);
      }

      .change.negative {
        background: var(--sl-color-danger-50);
        border-color: var(--sl-color-danger-300);
      }

      .change.negative .amount {
        color: var(--sl-color-danger-700);
      }

      .footer-buttons {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;
      }
    `,
  ];

  // Sale total (₱). Required.
  @property({ type: Number }) total = 0;

  // Whether the dialog is open. Bound by parent so a single instance can
  // be reused across cash sales.
  @property({ type: Boolean, reflect: true }) open = false;

  // Loading flag from the parent — when the parent is mid-submit, we
  // disable the Ring-up button to prevent double-submits.
  @property({ type: Boolean }) busy = false;

  // Sale-failure message from the parent. When a Ring-up fails server-side
  // (e.g. the sample-shop daily cap), the parent keeps this dialog open and
  // passes the reason here so the cashier sees it where they're acting —
  // not stranded on the order pane behind a full-page dialog.
  @property() error: string | null = null;

  // The running cash-given amount. Updated either by free typing in the
  // input (parsed from the DOM value) or by tapping a denomination button.
  // Reset clears it to 0.
  //
  // CRITICAL: this state is NEVER bound back to the input via `.value=...`.
  // Re-binding on every render fights the user's cursor: a keystroke
  // updates _amountGiven → re-render → Lit re-applies `.value="123.00"`
  // → cursor jumps, ".00" gets injected mid-typing, etc. Instead, we
  // treat the input's DOM value as authoritative for typing, and only
  // *push* into it imperatively via the ref when an external action
  // (denomination tap, Reset, dialog open) wants to override what the
  // user typed.
  @state() private _amountGiven = 0;

  private _inputRef: Ref<SlInputLike> = createRef();

  private get _change(): number {
    return Math.round((this._amountGiven - this.total) * 100) / 100;
  }

  // Updates both the state value AND the input's DOM value imperatively.
  // Used by the denomination buttons, Reset, and the open-transition reset.
  private _setAmount(value: number) {
    const rounded = Math.round(value * 100) / 100;
    this._amountGiven = Math.max(0, rounded);
    const input = this._inputRef.value;
    if (input) {
      input.value = this._amountGiven > 0 ? this._amountGiven.toFixed(2) : '';
    }
  }

  private _addDenom(value: number) {
    this._setAmount(this._amountGiven + value);
  }

  private _reset() {
    this._setAmount(0);
  }

  private _onInput(e: Event) {
    // Parse the user's typed value into _amountGiven for the change
    // calculation. We do NOT write back to the input — the user is
    // editing it and the DOM is authoritative for the duration.
    const raw = (e.target as HTMLInputElement).value;
    const n = Number.parseFloat(raw);
    this._amountGiven =
      !Number.isFinite(n) || n < 0 ? 0 : Math.round(n * 100) / 100;
  }

  private _confirm() {
    if (this._change < 0) return;
    this.dispatchEvent(
      new CustomEvent<{ amountGiven: number; change: number }>(
        'cash-confirmed',
        {
          detail: { amountGiven: this._amountGiven, change: this._change },
          bubbles: true,
          composed: true,
        }
      )
    );
  }

  private _cancel() {
    this.dispatchEvent(
      new CustomEvent('cash-cancelled', { bubbles: true, composed: true })
    );
  }

  // Reset internal state whenever the dialog transitions from closed → open
  // so a previous sale's denominations don't leak into the next. We update
  // _amountGiven here; the input element doesn't exist yet on the first
  // open render, so we sync its DOM value in updated() once the ref binds.
  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('open') && this.open && !changed.get('open')) {
      this._amountGiven = 0;
    }
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('open') && this.open && this._inputRef.value) {
      // Push the cleared state into the input DOM after the first render
      // of the now-open dialog has wired up the ref.
      this._inputRef.value.value = '';
    }
  }

  render() {
    const change = this._change;
    const canRingUp = change >= 0 && this._amountGiven > 0;
    const changeStr = money(Math.abs(change));
    const changeClass =
      change < 0 ? 'negative' : this._amountGiven > 0 ? 'positive' : '';

    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Cash payment"
        ?open=${this.open}
        @sl-request-close=${(e: Event) => {
          e.preventDefault();
          this._cancel();
        }}
      >
        ${
          this.error
            ? html`<sl-alert variant="danger" open style="margin-bottom: 12px;">
                ${this.error}
              </sl-alert>`
            : null
        }

        <div class="total-line">
          <span class="label">Total due</span>
          <span class="amount">${money(this.total)}</span>
        </div>

        <sl-input
          ${ref(this._inputRef)}
          label="Cash given"
          type="number"
          inputmode="decimal"
          min="0"
          step="0.01"
          placeholder="0.00"
          @sl-input=${this._onInput}
        ></sl-input>

        <div class="denoms">
          ${currencyDenominations().map(
            (v) => html`
              <sl-button
                variant="primary"
                size="medium"
                @click=${() => this._addDenom(v)}
              >
                +${activeCurrency().symbol}${v}
              </sl-button>
            `
          )}
        </div>

        <div class="reset-row">
          <sl-button size="small" variant="text" @click=${this._reset}>
            Reset
          </sl-button>
        </div>

        <div class="change ${changeClass}">
          <span class="label">${change < 0 ? 'Short' : 'Change'}</span>
          <span class="amount">${changeStr}</span>
        </div>

        <div slot="footer" class="footer-buttons">
          <sl-button
            variant="text"
            ?disabled=${this.busy}
            @click=${this._cancel}
          >
            Cancel
          </sl-button>
          <sl-button
            variant="primary"
            ?loading=${this.busy}
            ?disabled=${!canRingUp || this.busy}
            @click=${this._confirm}
          >
            Ring up
          </sl-button>
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cash-register-dialog': CashRegisterDialog;
  }
}
